import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import flatten from 'lodash/flatten';
import { from, mergeMap, of, toArray } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { LoadStatus } from '@capital-access/common/utils';
import { AccountUsersRepository } from '../account-users.repository';
import { loadAccountUsersFail, loadAccountUsersSuccess, tryLoadAccountUsers } from './account-users.actions';
import { loadAccountUsersStart } from './account-users.actions.internal';
import { getAccountUsersLoadStatus } from './account-users.selectors';

@Injectable()
export class AccountUsersEffects {
  private readonly maxConcurrentRequests = 5;
  private readonly take = 100;

  tryLoadAccountUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tryLoadAccountUsers),
      withLatestFrom(this.store.select(getAccountUsersLoadStatus)),
      filter(([, status]) => status == LoadStatus.Pristine || status == LoadStatus.Failed),
      map(() => loadAccountUsersStart())
    )
  );

  loadAccountUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccountUsersStart),
      switchMap(() =>
        this.getAccountUsers().pipe(
          map(users => loadAccountUsersSuccess({ users })),
          catchError(error => of(loadAccountUsersFail({ error })))
        )
      )
    )
  );

  private getAccountUsers() {
    return this.repository.getAccountUsers(0, this.take).pipe(
      switchMap(response => {
        if (response.totalCount === response.result.length) {
          return of(response.result);
        }

        const requestCount = Math.ceil((response.totalCount - this.take) / this.take);
        const skips = new Array(requestCount).fill(this.take).map((v, index) => v * (index + 1));

        return from(skips).pipe(
          mergeMap(skip => this.repository.getAccountUsers(skip, this.take), this.maxConcurrentRequests),
          toArray(),
          map(responses => [...response.result, ...flatten(responses.map(r => r.result))])
        );
      })
    );
  }

  constructor(private actions$: Actions, private store: Store, private repository: AccountUsersRepository) {}
}
